/**
 * Alshallal Pastry Company HR System - Session Timeout Warning
 * Shows warning banner when session is about to expire.
 * For HR Admin and Super Admin users.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, Clock, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SessionTimeoutWarning = () => {
  const { user, token, logout } = useAuth();
  const { isRTL } = useLanguage();
  
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Only show for HR Admin and Super Admin
  const shouldShowWarning = user && ['hr_admin', 'super_admin'].includes(user.role);

  const checkSession = useCallback(async () => {
    if (!token || !shouldShowWarning || dismissed) return;

    try {
      const response = await axios.get(`${API_URL}/auth/session/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { valid, seconds_until_timeout, show_warning, reason } = response.data;

      if (!valid) {
        // Session expired - logout
        if (reason === 'inactivity_timeout' || reason === 'token_expired') {
          logout();
        }
        return;
      }

      if (show_warning && seconds_until_timeout > 0) {
        setShowWarning(true);
        setSecondsRemaining(seconds_until_timeout);
      } else {
        setShowWarning(false);
      }
    } catch (error) {
      console.error('Session check error:', error);
    }
  }, [token, shouldShowWarning, dismissed, logout]);

  // Check session every 30 seconds
  useEffect(() => {
    if (!shouldShowWarning) return;

    checkSession();
    const interval = setInterval(checkSession, 30000);
    
    return () => clearInterval(interval);
  }, [checkSession, shouldShowWarning]);

  // Countdown timer when warning is shown
  useEffect(() => {
    if (!showWarning || secondsRemaining <= 0) return;

    const timer = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showWarning, secondsRemaining, logout]);

  const handleExtendSession = async () => {
    try {
      await axios.post(`${API_URL}/auth/session/extend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowWarning(false);
      setDismissed(false);
    } catch (error) {
      console.error('Failed to extend session:', error);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowWarning(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!showWarning || !shouldShowWarning) return null;

  return (
    <div 
      className={cn(
        "fixed top-16 z-50 p-4",
        isRTL ? "left-0 right-64" : "left-64 right-0"
      )}
      data-testid="session-timeout-warning"
    >
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between shadow-lg animate-in">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-full">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-amber-800">
              {isRTL ? 'تحذير انتهاء الجلسة' : 'Session Timeout Warning'}
            </p>
            <p className="text-sm text-amber-700 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {isRTL 
                ? `ستنتهي جلستك خلال ${formatTime(secondsRemaining)}`
                : `Your session will expire in ${formatTime(secondsRemaining)}`
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleExtendSession}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="extend-session-btn"
          >
            {isRTL ? 'تمديد الجلسة' : 'Stay Logged In'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            className="text-amber-600 hover:text-amber-800"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
